/**
 * Adding up what a set of planned roles would cost.
 *
 * The rates below are approximate and hand-maintained, and that is the whole
 * design rather than a shortcut. A headcount plan is decided weeks before anyone
 * is hired and months before anyone is paid, so a live exchange rate would be
 * false precision — and fetching one would mean this package doing I/O, which is
 * the one thing it does not do.
 *
 * What the numbers are for is "is this plan roughly a million dollars or roughly
 * three", and they are good enough for that. Anything that needs to be exact
 * belongs in the finance system, not in an org chart.
 */
/**
 * Approximate units of currency per US dollar, mid-2026.
 *
 * Update them when they have drifted enough to change a decision. A currency
 * that is not here is treated as unconvertible rather than as one-to-one, which
 * is why `approximateUsd` can return null: a wrong total is worse than an
 * incomplete one, because nobody checks a number that looks plausible.
 */
export const RATES_PER_USD = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.78,
    CAD: 1.36,
    AUD: 1.5,
    CHF: 0.88,
    SEK: 10.5,
    NOK: 10.7,
    DKK: 6.9,
    PLN: 3.95,
    RON: 4.57,
    CZK: 22.8,
    HUF: 355,
    ALL: 92,
    RSD: 108,
    MKD: 56.5,
    BAM: 1.8,
    TRY: 34,
    ILS: 3.7,
    AED: 3.67,
    ZAR: 18.3,
    EGP: 48,
    KES: 130,
    NGN: 1550,
    INR: 83.5,
    CNY: 7.2,
    JPY: 152,
    KRW: 1340,
    SGD: 1.34,
    HKD: 7.8,
    TWD: 32,
    MYR: 4.4,
    THB: 34.5,
    IDR: 15800,
    PHP: 57,
    VND: 25400,
    MXN: 18.5,
    BRL: 5.4,
    ARS: 990,
    CLP: 940,
    COP: 4100,
    PEN: 3.75,
    NZD: 1.64,
};
/** The currencies this can convert, for a picker to offer. */
export const CURRENCIES = Object.keys(RATES_PER_USD);
/**
 * Roughly what this is in US dollars, or null if the currency is not one we
 * have a rate for.
 */
export function approximateUsd(cost) {
    if (!cost || !Number.isFinite(cost.amount))
        return null;
    const rate = RATES_PER_USD[cost.currency.toUpperCase()];
    if (!rate)
        return null;
    return cost.amount / rate;
}
/** Add up what a set of roles would cost, in approximate US dollars. */
export function totalCost(costs) {
    let usd = 0;
    let counted = 0;
    const unconverted = [];
    for (const cost of costs) {
        if (!cost || !Number.isFinite(cost.amount))
            continue;
        const converted = approximateUsd(cost);
        if (converted === null) {
            unconverted.push(cost);
            continue;
        }
        usd += converted;
        counted += 1;
    }
    return { usd, counted, unconverted };
}
/**
 * An amount as somebody would write it, in its own currency.
 *
 * No decimals: these are approximate salaries, and "€92,000.00" claims a
 * precision the number does not have.
 */
export function formatMoney(cost) {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: cost.currency,
            maximumFractionDigits: 0,
        }).format(cost.amount);
    }
    catch {
        // An unknown currency code is still worth showing as a number.
        return `${cost.currency} ${Math.round(cost.amount).toLocaleString('en-US')}`;
    }
}
//# sourceMappingURL=money.js.map